import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Search, X, RotateCcw, Loader2, CreditCard, Banknote } from 'lucide-react';
import { sellerApi } from '../sellerAuth';
import { money } from './helpers';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'succeeded', label: 'Succeeded' },
  { key: 'refunded', label: 'Refunded' },
  { key: 'cash', label: 'Cash' },
];

const StatusPill = ({ status }) => {
  const map = {
    succeeded: 'bg-primary/15 text-primary',
    refunded: 'bg-muted text-muted-foreground',
    partially_refunded: 'bg-orange-400/15 text-orange-500',
  };
  const label = status === 'partially_refunded' ? 'Partial refund' : status === 'refunded' ? 'Refunded' : 'Succeeded';
  return <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${map[status] || map.succeeded}`}>{label}</span>;
};

const ActivityScreen = ({ transactions = [], onReload }) => {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [sel, setSel] = useState(null);
  const [refundAmt, setRefundAmt] = useState('');
  const [busy, setBusy] = useState(false);

  const list = useMemo(() => {
    return transactions.filter((t) => {
      if (filter === 'succeeded' && t.status !== 'succeeded') return false;
      if (filter === 'refunded' && !String(t.status).includes('refund')) return false;
      if (filter === 'cash' && t.method !== 'cash') return false;
      if (q && !(`${t.note || ''} ${t.method} ${t.net}`.toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    });
  }, [transactions, filter, q]);

  const openTxn = (t) => { setSel(t); setRefundAmt(''); };

  const doRefund = async () => {
    setBusy(true);
    try {
      const body = refundAmt ? { amount: parseFloat(refundAmt) } : {};
      const res = await sellerApi.post(`/seller/transactions/${sel.id}/refund`, body);
      toast.success(res.data.status === 'refunded' ? 'Fully refunded' : 'Partial refund issued');
      setSel(null);
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refund failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="px-5 pt-6">
      <h1 className="font-heading text-3xl font-black tracking-tight">Activity</h1>

      <div className="mt-4 flex items-center gap-2 rounded-full bg-card border border-border px-4 py-2.5">
        <Search size={16} className="text-muted-foreground" />
        <input data-testid="activity-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search transactions" className="flex-1 bg-transparent outline-none text-sm" />
      </div>

      <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => setFilter(f.key)} className={`px-4 py-1.5 rounded-full text-sm font-bold border whitespace-nowrap transition-colors ${filter === f.key ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}>{f.label}</button>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {list.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center rounded-3xl bg-card border border-border">No transactions found.</p>
        ) : list.map((t) => (
          <button key={t.id} data-testid={`txn-${t.id}`} onClick={() => openTxn(t)} className="w-full flex items-center justify-between rounded-2xl bg-card border border-border px-4 py-3 text-left active:scale-[0.99] transition-transform">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-primary">{t.method === 'cash' ? <Banknote size={18} strokeWidth={2.5} /> : <CreditCard size={18} strokeWidth={2.5} />}</div>
              <div>
                <p className="text-sm font-bold capitalize">{t.method} sale</p>
                <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString()}</p>
              </div>
            </div>
            <div className="text-right">
              <p className={`font-heading font-bold ${String(t.status).includes('refund') ? 'text-muted-foreground' : 'text-foreground'}`}>{money(t.net)}</p>
              <StatusPill status={t.status} />
            </div>
          </button>
        ))}
      </div>

      {/* Detail sheet */}
      {sel && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSel(null)} />
          <div className="relative w-full max-w-md bg-background border-t border-border rounded-t-3xl p-6 pb-10 rise-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-black capitalize">{sel.method} sale</h3>
              <button onClick={() => setSel(null)} className="text-muted-foreground"><X size={22} /></button>
            </div>
            <div className="rounded-2xl bg-card border border-border p-4 space-y-1.5 text-sm">
              <Line label="Amount" value={money(sel.amount)} />
              {sel.tax > 0 && <Line label="Tax" value={money(sel.tax)} />}
              {sel.tip > 0 && <Line label="Tip" value={money(sel.tip)} />}
              {sel.fee > 0 && <Line label="Platform fee" value={money(sel.fee)} muted />}
              {sel.stripe_fee > 0 && <Line label="Stripe fee" value={money(sel.stripe_fee)} muted />}
              <div className="border-t border-dashed border-border my-1.5" />
              <Line label="You received" value={money(sel.net)} bold />
              {sel.refunded_amount > 0 && <Line label="Refunded" value={money(sel.refunded_amount)} muted />}
              <div className="pt-1"><StatusPill status={sel.status} /></div>
            </div>

            {sel.method === 'card' && sel.payment_intent_id && !String(sel.status).includes('refund') || (sel.method === 'card' && sel.status === 'partially_refunded') ? (
              <div className="mt-4">
                <input data-testid="refund-amount-input" type="number" min="0" step="0.5" value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)} placeholder={`Refund amount (blank = full ${money(sel.net - (sel.refunded_amount || 0))})`}
                  className="w-full rounded-2xl bg-card border border-border px-4 py-3 text-sm outline-none focus:border-primary transition" />
                <button data-testid="refund-submit-button" disabled={busy} onClick={doRefund} className="mt-2 w-full h-12 rounded-full bg-destructive text-destructive-foreground font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50">
                  {busy ? <Loader2 size={18} className="animate-spin" /> : <><RotateCcw size={17} strokeWidth={2.5} /> Issue refund</>}
                </button>
              </div>
            ) : sel.method === 'cash' ? (
              <p className="mt-4 text-xs text-muted-foreground text-center">Cash sales can't be refunded through FLIX.</p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

const Line = ({ label, value, muted, bold }) => (
  <div className="flex justify-between">
    <span className={muted ? 'text-muted-foreground' : bold ? 'font-heading font-bold' : 'text-foreground/80'}>{label}</span>
    <span className={muted ? 'text-muted-foreground' : bold ? 'font-heading font-bold text-primary' : 'text-foreground/80'}>{value}</span>
  </div>
);

export default ActivityScreen;
